import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {hoverSelector} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';

/*
 * Props.
 */

interface RecurrenceWeekdayButtonProps {
  weekdayNumber: number;
  isSelected: boolean;
  onClick: Handler;
}

/*
 * Styles.
 */

interface RecurrenceWeekdayButtonStyleProps {
  $isSelected: boolean;
}

const StyledButtonWrapperDiv = styled.div<RecurrenceWeekdayButtonStyleProps>`
  text-align: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;

  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};

  color: ${(p) => (p.$isSelected ? p.theme.greys.white : p.theme.greys.shade70)};
  background-color: ${(p) =>
    p.$isSelected ? p.theme.palette[PaletteColorsEnum.BLUE].shade40 : p.theme.greys.shade20};

  ${hoverSelector} {
    color: ${(p) => (p.$isSelected ? p.theme.greys.white : p.theme.greys.shade80)};
    background-color: ${(p) =>
      p.$isSelected ? p.theme.palette[PaletteColorsEnum.BLUE].shade50 : p.theme.greys.shade40};
  }
`;

const StyledButtonContentDiv = styled.div`
  padding: 7px;
`;

/*
 * Component.
 */

const weekdayNumberToShortLabel: {[key: number]: ReactNode} = {
  1: <Trans id="custom-recurrence-monday-short">M</Trans>,
  2: <Trans id="custom-recurrence-tuesday-short">T</Trans>,
  3: <Trans id="custom-recurrence-wednesday-short">W</Trans>,
  4: <Trans id="custom-recurrence-thursday-short">T</Trans>,
  5: <Trans id="custom-recurrence-friday-short">F</Trans>,
  6: <Trans id="custom-recurrence-saturday-short">S</Trans>,
  7: <Trans id="custom-recurrence-sunday-short">S</Trans>,
} as const;

const weekdayNumberToLongLabel: {[key: number]: ReactNode} = {
  1: <Trans id="custom-recurrence-monday-long">Monday</Trans>,
  2: <Trans id="custom-recurrence-tuesday-long">Tuesday</Trans>,
  3: <Trans id="custom-recurrence-wednesday-long">Wednesday</Trans>,
  4: <Trans id="custom-recurrence-thursday-long">Thursday</Trans>,
  5: <Trans id="custom-recurrence-friday-long">Friday</Trans>,
  6: <Trans id="custom-recurrence-saturday-long">Saturday</Trans>,
  7: <Trans id="custom-recurrence-sunday-long">Sunday</Trans>,
} as const;

export const RecurrenceWeekdayButton: FC<RecurrenceWeekdayButtonProps> = (props) => (
  <TooltipCoordinator render={() => weekdayNumberToLongLabel[props.weekdayNumber]}>
    <ChromelessButton key={props.weekdayNumber} onClick={props.onClick}>
      <StyledButtonWrapperDiv $isSelected={props.isSelected}>
        <StyledButtonContentDiv>{weekdayNumberToShortLabel[props.weekdayNumber]}</StyledButtonContentDiv>
      </StyledButtonWrapperDiv>
    </ChromelessButton>
  </TooltipCoordinator>
);
